import { LogUtils } from "@repo/shared-utils/log-utils";
import { fetchData } from "@repo/shared-utils/fetch-utils";
import { fetchDataServer } from "@repo/shared-utils/server-fetch-utils";
import { AMCApiEndpoints } from "../constants/AMCApiEndpoints";
import {
  IAmcContent,
  IAmcDetails,
  IFundsHolding,
  ISchemeSectorResponse,
  ISimilarSchemesResponse,
} from "../interfaces/AMCChartData.interface";
import { getUtmParamsFromCookie, getPageType } from "@repo/shared-utils/common-utils";

type AMCListing = {
  bseSchCode: string;
  amcSlug: string;
  amcCode: number;
  amcName: string;
  schCode: number;
  schName: string;
};

interface Cache {
  dataMap: Map<string, string>;
  lastFetched: number; // timestamp in ms
}

let cache: Cache | null = null;

/**
 * getAmcChartData
 * @param param0
 * @returns
 */
export const getAmcChartData = async ({
  bseSchCode,
  duration = "1M",
}: {
  bseSchCode: string;
  duration: string;
}) => {
  const _body = { duration, bseSchCode };
  const endpoint = AMCApiEndpoints?.AMC_CHART;
  const response = await fetchData({
    options: { method: "POST" },
    endpoint,
    body: _body,
    cache: "no-store",
  });

  LogUtils.debug("getAmcChartData:--", response);
  return response;
};

/**
 * getAMCContent
 * @param version
 * @returns
 */
export const getAMCContent = async (version: string): Promise<IAmcContent> => {
  const endpoint = AMCApiEndpoints.AMC_CONTENT.replace("{version}", version);
  const response = (await fetchData({
    endpoint,
    tags: ["AMCPDPContent", `AMCPDPContent${version}`],
    cache: "force-cache",
  })) as IAmcContent;
  LogUtils.debug("getAMCContent: ", response);
  return response;
};

/**
 * getAMCDetails
 * @param bseSchCode
 * @param offset
 * @param limit
 * @returns
 */
export const getAMCDetails = async (
  bseSchCode: string,
  offset: number = 1,
  limit: number = 7
): Promise<{ data: IAmcDetails }> => {
  const _body = {
    offset,
    limit,
    bseSchCode,
  };

  const endpoint = AMCApiEndpoints?.AMC_DETAILS;
  const response = await fetchDataServer({
    endpoint,
    body: _body,
    cache: "no-store",
  });
  LogUtils.debug("getAMCDetails:--- ", response);
  return response?.data;
};

/**
 * getSimilarSchemes
 * @param bseSchCode
 * @param offset
 * @param limit
 * @returns
 */
export const getSimilarSchemes = async (
  bseSchCode: string,
  offset: number = 1,
  limit: number = 10
): Promise<ISimilarSchemesResponse> => {
  const _body = {
    offset,
    limit,
    bseSchCode,
  };

  const endpoint = AMCApiEndpoints?.AMC_SIMILAR_SCHEMES;
  const response = await fetchDataServer({
    endpoint,
    body: _body,
    cache: "no-store",
  });
  LogUtils.debug("getSimilarSchemes:--- ", response);
  return response;
};

/**
 * getTopFundsHolding
 * @param bseSchCode
 * @param offset
 * @param limit
 * @returns
 */
export const getTopFundsHolding = async (
  bseSchCode: string,
  offset: number = 1,
  limit: number = 6
): Promise<IFundsHolding> => {
  const _body = {
    offset,
    limit,
    bseSchCode,
  };

  const endpoint = AMCApiEndpoints?.AMC_TOP_FUNDS_HOLDING;
  const response = await fetchDataServer({
    endpoint,
    body: _body,
    cache: "no-store",
  });
  LogUtils.debug("getFundsHolding:--- ", response);
  return response?.data;
};

/**
 * getSectorFundsHolding
 * @param bseSchCode
 * @param offset
 * @param limit
 * @returns
 */
export const getSectorFundsHolding = async (
  bseSchCode: string,
  offset: number = 1,
  limit: number = 6
): Promise<ISchemeSectorResponse> => {
  const _body = {
    offset,
    limit,
    bseSchCode,
  };

  const endpoint = AMCApiEndpoints?.AMC_SECTOR_FUNDS_HOLDING;
  const response = await fetchDataServer({
    endpoint,
    body: _body,
    cache: "no-store",
  });
  LogUtils.debug("getSectorFundsHolding:--- ", response);
  return response;
};

/**
 * fetchInitialListing
 * @param symbol
 * @returns
 */
export const fetchInitialListing = async () => {
  try {
    const response = await fetchData({
      endpoint: AMCApiEndpoints.INITIAL_LISTING_DATA,
      cache: "no-store",
      tags: ["listing"],
    });
    const dataMap: Map<string, string> = new Map();
    response?.data?.items?.forEach((i: AMCListing) => {
      const { amcSlug, bseSchCode } = i;
      dataMap.set(amcSlug, bseSchCode);
    });
    LogUtils.debug("fetchInitialListing: ", response?.data);
    return { dataMap, lastFetched: Date.now() };
  } catch (error) {
    console.error("Error fetching initial listing data:", error);
    throw error;
  }
};

/**
 * Gets the timestamp for today's 7:00 PM IST in milliseconds.
 * 7:00 PM IST = 13:30 UTC
 * @returns number - Timestamp for today's 7:00 PM IST in milliseconds
 */
function getTodayRefreshTimestampIST(): number {
  const now = new Date();

  // Current date in UTC
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  const day = now.getUTCDate();

  // 7:00 PM IST = 13:30 UTC
  const refreshUTC = Date.UTC(year, month, day, 13, 30, 0);
  return refreshUTC;
}

/**
 * Retrieves the listing cache for stocks.
 * @returns Promise that resolves to a map of stock listings
 */
export async function getListingCache(): Promise<Map<string, string>> {
  const refreshTime = getTodayRefreshTimestampIST();
  // Lazy reload if cache is missing or last fetch was before today's 9:45
  if (!cache || cache.lastFetched < refreshTime) {
    console.log("Refreshing listing cache at: ", new Date(refreshTime).toLocaleString());
    cache = await fetchInitialListing();
  }

  if (!cache) {
    throw new Error("Failed to fetch listing cache.");
  }
  return cache?.dataMap;
}

/**
 * getBseSchCode
 * Returns bseSchoCode based on amc slug
 * @param amcSlug
 * @returns
 */
export async function getBseSchCode(amcSlug: string) {
  return (await getListingCache()).get(amcSlug);
}

/**
 * normalizeSchemeName
 * @description Takes name of amc scheme and converts it into hyphen separated slug
 * @param name
 * @returns amcSlug
 */
export const normalizeSchemeName = (name: string): string => {
  if (!name) {
    return "";
  }
  let n = name.toLowerCase();
  n = n.replace(/\bgrowth\b/g, "");
  n = n.replace(/[^a-z0-9\s]/g, " ");
  n = n.replace(/\s+/g, " ").trim();
  let slug = n.replace(/\s/g, "-");
  slug = slug.replace(/-+/g, "-");
  slug = `${slug}-growth`;
  return slug;
};

// API function to validate OTP
export const validateMobileOtp = async (mobile: string, mobpswd: string): Promise<unknown> => {
  const payload = {
    mobpswd: mobpswd,
    mobile: mobile,
    utmCampaign: "pdp",
    fullname: "",
  };
  const { bfslVerifyOtp } = await import("@repo/sso/bfslLoginApi");

  try {
    const bfslVerifyOtpResponse = await bfslVerifyOtp(payload);
    return bfslVerifyOtpResponse;
  } catch (e) {
    console.error("Error sending OTP:", e);
    throw e;
  }
};

// API function to save lead
export const saveLead = async (mobile: string): Promise<unknown> => {
  const payload = {
    utmCampaign: "pdp",
    name: "",
    mobile: mobile,
  };
  const { bfslSaveLead } = await import("@repo/sso/bfslLoginApi");

  try {
    const bfslSaveLeadResponse = await bfslSaveLead(payload);
    return bfslSaveLeadResponse;
  } catch (e) {
    console.error("Error saving lead:", e);
  }
};

// API function to send OTP
export const sendMobileOtp = async (mobileNumber: string): Promise<unknown> => {
  const utm = getUtmParamsFromCookie();
  const payload = {
    name: "",
    mobile: mobileNumber,
    refferalCode: "",
    utmCampaign: utm?.bflpageType || getPageType("tag-1") || "NA",
  };
  const { bfslSendOtp } = await import("@repo/sso/bfslLoginApi");

  try {
    const bfslSendOtpResponse = await bfslSendOtp(payload);
    return bfslSendOtpResponse;
  } catch (e) {
    console.error("Error sending OTP:", e);
    throw e;
  }
};
